import React, { useState, useEffect, useRef } from "react";
import {
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  ConfirmationResult,
  sendPasswordResetEmail,
  fetchSignInMethodsForEmail,
  User as FirebaseUser,
} from "firebase/auth";
import { auth, firebaseConfig } from "../../firebase";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, LoginOtpChallenge } from "../../context/AuthContext";
import LoginOtpStep from "./LoginOtpStep";
import { useRecaptchaVerifier } from "../../hooks/reCaptchahook";
import { socialSignInEnabled } from "../../utils/features";

// Extend window for OTP
declare global {
  interface Window {
    recaptchaVerifier?: RecaptchaVerifier;
    confirmationResult?: ConfirmationResult;
  }
}

interface LoginProps {
  onSuccess?: () => void;
  onClose?: () => void;
}

/** Map Firebase Auth error codes to user-friendly messages. */
function getAuthErrorMessage(err: any): string {
  const code: string = err?.code ?? "";
  if (code === "auth/network-request-failed") {
    return "Network error: unable to reach authentication servers. Please check your internet connection and try again.";
  }
  if (code === "auth/wrong-password" || code === "auth/invalid-credential") {
    return "Incorrect email or password. Please try again.";
  }
  if (code === "auth/user-not-found") {
    return "No account found with this email.";
  }
  if (code === "auth/too-many-requests") {
    return "Too many failed attempts. Please try again later.";
  }
  if (code === "auth/popup-closed-by-user") {
    return "Sign-in popup was closed. Please try again.";
  }
  return err?.message || "An unexpected error occurred. Please try again.";
}

interface Country {
  name: string;
  dial_code: string;
  code: string;
  flag: string;
}

const Login: React.FC<LoginProps> = ({ onSuccess, onClose }) => {
  const { user, loading, ensureSession, requestLoginOtp, setOauthAccessToken } = useAuth();

  // Set once the password is accepted but before a session exists — the code
  // step stands between the two.
  const [otpChallenge, setOtpChallenge] = useState<LoginOtpChallenge | null>(null);
  const [otpUser, setOtpUser] = useState<FirebaseUser | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const justVerified = searchParams.get("verified") === "true";

  // Password visibility state
  const [showPassword, setShowPassword] = useState(false);

  /**
   * Was this person already signed in when the screen opened?
   *
   * Only that case gets bounced away, and `user` on its own cannot identify it.
   * Firebase sets `user` the moment it accepts the *password* — before the code
   * has even been asked for — so redirecting on `user` tore this screen down
   * mid-sign-in: the code was sent, the phone buzzed, and the box to type it
   * into never appeared, because the component that renders it had already
   * unmounted and `setOtpChallenge` landed on nothing.
   *
   * Nothing else needs this effect. Every handler below navigates explicitly
   * once it has finished its own flow.
   */
  const arrivedSignedIn = useRef<boolean | null>(null);

  useEffect(() => {
    // Firebase has not reported yet; `user` is null for a reason that has
    // nothing to do with whether this person is signed in.
    if (loading) return;

    if (arrivedSignedIn.current === null) {
      arrivedSignedIn.current = !!user;
    }

    if (arrivedSignedIn.current && user) {
      if (onSuccess) {
        onSuccess();
      } else {
        navigate("/");
      }
    }
  }, [loading, user, navigate, onSuccess]);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [loadingState, setLoadingState] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState("");
  const [showPhoneLogin, setShowPhoneLogin] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [countries, setCountries] = useState<Country[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<Country>({
    name: "United Arab Emirates",
    dial_code: "+971",
    code: "AE",
    flag: "🇦🇪",
  });
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);

  useRecaptchaVerifier("recaptcha-container");

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await fetch(
          "https://restcountries.com/v3.1/all?fields=name,idd,cca2,flag"
        );
        const data = await response.json();
        const formattedCountries = data
          .filter((country: any) => country.idd?.root)
          .map((country: any) => ({
            name: country.name.common,
            dial_code: country.idd.root + (country.idd.suffixes?.[0] || ""),
            code: country.cca2,
            flag: country.flag,
          }))
          .sort((a: Country, b: Country) => a.name.localeCompare(b.name));
        setCountries(formattedCountries);
      } catch (err) {
        console.error("Failed to fetch countries:", err);
      }
    };
    fetchCountries();
  }, []);

  // Email/Password Login
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoadingState(true);
    try {
      const signInUrl = `https://identitytoolkit.googleapis.com/v1/accounts:signInWithPassword?key=${firebaseConfig.apiKey}`;
      const signInResponse = await fetch(signInUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          password,
          returnSecureToken: true,
        }),
      });

      const signInData = await signInResponse.json();

      if (!signInResponse.ok) {
        const error: any = new Error(
          signInData.error?.message || "Authentication failed."
        );
        error.code =
          signInData.error?.message === "EMAIL_NOT_FOUND"
            ? "auth/user-not-found"
            : signInData.error?.message === "INVALID_PASSWORD" ||
              signInData.error?.message === "INVALID_LOGIN_CREDENTIALS"
            ? "auth/wrong-password"
            : signInData.error?.message === "TOO_MANY_ATTEMPTS_TRY_LATER"
            ? "auth/too-many-requests"
            : "auth/" + (signInData.error?.message || "unknown");
        throw error;
      }

      if (signInData.kind === "identitytoolkit#VerifyPasswordResponse") {
        const tokenUrl = `https://securetoken.googleapis.com/v1/token?key=${firebaseConfig.apiKey}`;
        const tokenResponse = await fetch(tokenUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            grant_type: "refresh_token",
            refresh_token: signInData.refreshToken,
          }),
        });

        const tokenData = await tokenResponse.json();

        if (tokenResponse.ok && tokenData.access_token) {
          setOauthAccessToken(tokenData.access_token);

          const isProd = process.env.NODE_ENV === "production";
          const maxAge = tokenData.expires_in
            ? parseInt(tokenData.expires_in, 10)
            : isProd
            ? 60 * 60 * 24
            : 60 * 60 * 24 * 5;

          const cookieOptions = [
            `oauthAccessToken=${tokenData.access_token}`,
            `max-age=${maxAge}`,
            `path=/`,
            isProd ? "secure" : "",
            isProd ? "samesite=none" : "samesite=lax",
          ]
            .filter(Boolean)
            .join("; ");
          document.cookie = cookieOptions;
        }
      }

      const result = await signInWithEmailAndPassword(auth, email, password);

      // Password accepted. Staff need a code before any session is issued; for
      // everyone else this comes back otpRequired:false and nothing changes.
      const challenge = await requestLoginOtp(result.user);
      if (challenge.otpRequired) {
        setOtpUser(result.user);
        setOtpChallenge(challenge);
        setLoadingState(false);
        return;
      }

      const ok = await ensureSession(result.user);
      if (!ok) {
        setError("Server is currently unavailable. Please try again later.");
        setLoadingState(false);
        return;
      }

      setLoginSuccess(true);
      setTimeout(() => {
        if (onSuccess) onSuccess();
        else navigate("/");
        if (onClose) onClose();
      }, 1200);
    } catch (err: any) {
      setError(getAuthErrorMessage(err));
    }
    setLoadingState(false);
  };

  // Google Login
  const handleGoogleLogin = async () => {
    setError("");
    setLoadingState(true);
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);

      // Staff need a code before any session is issued. Same second step the
      // password path takes — without it the server refuses, and the person is
      // told the server is unavailable when in fact it is waiting for a code.
      const challenge = await requestLoginOtp(result.user);
      if (challenge.otpRequired) {
        setOtpUser(result.user);
        setOtpChallenge(challenge);
        setLoadingState(false);
        return;
      }

      const ok = await ensureSession(result.user);
      if (!ok) {
        setError("Server is currently unavailable. Please try again later.");
        setLoadingState(false);
        return;
      }

      setLoginSuccess(true);
      setTimeout(() => {
        if (onSuccess) onSuccess();
        else navigate("/");
        if (onClose) onClose();
      }, 1200);
    } catch (err: any) {
      setError(getAuthErrorMessage(err));
    }
    setLoadingState(false);
  };

  // OTP Login
  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoadingState(true);

    if (!phone.trim()) {
      setError("Please enter your mobile phone number.");
      setLoadingState(false);
      return;
    }

    const fullPhoneNumber = selectedCountry.dial_code + phone;
    const appVerifier = window.recaptchaVerifier!;
    try {
      const confirmationResult = await signInWithPhoneNumber(
        auth,
        fullPhoneNumber,
        appVerifier
      );
      window.confirmationResult = confirmationResult;
      setOtpSent(true);
    } catch (err: any) {
      if (err.code === "auth/invalid-phone-number") {
        setError("Please enter a valid mobile phone number.");
      } else {
        setError(err.message);
      }
    }
    setLoadingState(false);
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoadingState(true);
    try {
      const userCredential = await window.confirmationResult!.confirm(otp);

      // Staff need a code before any session is issued. Same second step the
      // password path takes — without it the server refuses, and the person is
      // told the server is unavailable when in fact it is waiting for a code.
      const challenge = await requestLoginOtp(userCredential.user);
      if (challenge.otpRequired) {
        setOtpUser(userCredential.user);
        setOtpChallenge(challenge);
        setLoadingState(false);
        return;
      }

      const ok = await ensureSession(userCredential.user);
      if (!ok) {
        setError("Server is currently unavailable. Please try again later.");
        setLoadingState(false);
        return;
      }

      setLoginSuccess(true);
      setTimeout(() => {
        if (onSuccess) onSuccess();
        else navigate("/");
        if (onClose) onClose();
      }, 1200);
    } catch (err: any) {
      if (err.code?.includes("auth/") && err.code !== "auth/network-request-failed") {
        setError("Invalid OTP. Please try again.");
      } else {
        setError(getAuthErrorMessage(err));
      }
    }
    setLoadingState(false);
  };

  const handleForgotPassword = async () => {
    setResetMessage("");
    if (!email.trim()) {
      setResetMessage("Please enter your email address first.");
      return;
    }
    setResetLoading(true);
    try {
      const methods = await fetchSignInMethodsForEmail(auth, email);
      if (!methods || methods.length === 0) {
        setResetMessage("Email not found. Please sign up first.");
        setResetLoading(false);
        return;
      }
      await sendPasswordResetEmail(auth, email);
      setResetMessage("Password reset email sent. Check your inbox.");
    } catch (err: any) {
      setResetMessage(err.message || "Failed to send password reset email.");
    } finally {
      setResetLoading(false);
    }
  };

  const handleClose = () => {
    if (onClose) onClose();
    else navigate(-1);
  };

  const handleBackToEmailLogin = () => {
    setShowPhoneLogin(false);
    setOtpSent(false);
    setPhone("");
    setOtp("");
    setError("");
  };

  if (loading) return <div>Loading...</div>;

  /**
   * This screen is both the /login route and the modal the booking flow opens.
   *
   * Only the modal wants the fixed, dimmed overlay. On the route there is
   * nothing behind it to dim, so `fixed inset-0` covered an empty body — the
   * page came up white with the navbar hidden underneath it. `onClose` is how
   * handleClose already tells the two apart, so it decides this too.
   */
  const asModal = onClose !== undefined;

  return (
    <div
      className={
        asModal
          ? "fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
          : "flex items-center justify-center min-h-screen bg-card"
      }
    >
      <div className="bg-white p-8 rounded-xl shadow-md w-full max-w-sm relative">
        <button
          className="absolute top-2 right-2 text-2xl text-gray-400 hover:text-gray-600"
          onClick={handleClose}
          type="button"
          aria-label="Close"
        >
          ×
        </button>

        {loginSuccess ? (
          <div className="flex flex-col items-center justify-center h-40">
            <span className="text-green-600 text-2xl font-bold mb-2">
              Login Successful!
            </span>
            <span className="text-gray-500">Redirecting...</span>
          </div>
        ) : !showPhoneLogin ? (
          <div>
            <h2 className="text-2xl font-bold mb-6 text-primary">Login</h2>

            {justVerified && (
              <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-4 rounded shadow-md">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <p className="text-green-800 font-semibold text-sm">Email Verified!</p>
                    <p className="text-green-700 text-sm mt-1">Your email has been verified. You can now log in.</p>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4 rounded shadow-md">
                <div className="flex items-start">
                  <svg
                    className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div>
                    <p className="text-red-800 font-semibold text-sm">Unable to Login</p>
                    <p className="text-red-700 text-sm mt-1">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {otpChallenge && otpUser ? (
              <LoginOtpStep
                user={otpUser}
                challenge={otpChallenge}
                onVerified={() => {
                  setLoginSuccess(true);
                  setTimeout(() => {
                    if (onSuccess) onSuccess();
                    else navigate("/");
                    if (onClose) onClose();
                  }, 1200);
                }}
                onCancel={() => { setOtpChallenge(null); setOtpUser(null); }}
              />
            ) : (
            <form onSubmit={handleLogin}>
              <div className="mb-4">
                <label className="block mb-1 font-semibold">Email</label>
                <input
                  type="email"
                  className="w-full border rounded px-3 py-2"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              {/* Password Field with Custom SVGs */}
              <div className="mb-6">
                <label className="block mb-1 font-semibold">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    className="w-full border rounded px-3 py-2 pr-10"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      // Visible Icon (Eye Open)
                      <svg focusable="false" aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5">
                        <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5M12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5m0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3" />
                      </svg>
                    ) : (
                      // Hidden Icon (Visibility Off)
                      <svg
                        focusable="false"
                        aria-hidden="true"
                        viewBox="0 0 24 24"
                        className="h-5 w-5"
                      >
                        <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7M2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2m4.31-.78 3.15 3.15.02-.16c0-1.66-1.34-3-3-3z" />
                      </svg>
                    )}
                  </button>
                </div>

                <div className="flex items-center justify-between mt-2">
                  <button
                    type="button"
                    className="text-sm text-primary font-semibold"
                    onClick={handleForgotPassword}
                    disabled={resetLoading}
                  >
                    {resetLoading ? "Sending..." : "Forgot password?"}
                  </button>
                  {resetMessage && (
                    <div className="text-sm text-gray-600 ml-3">{resetMessage}</div>
                  )}
                </div>
              </div>

              <button
                type="submit"
                className="w-full bg-primary text-white py-2 rounded font-bold hover:bg-blue-800 transition"
                disabled={loadingState}
              >
                {loadingState ? "Logging in..." : "Login"}
              </button>
            </form>
            )}

            {/* Google & phone sign-in. Hidden by default — see
                socialSignInEnabled. Left whole rather than deleted: the
                handlers and the phone OTP screen below still work, so this
                comes back by setting one variable. */}
            {socialSignInEnabled && (
              <>
              {/* Google & Phone buttons */}
              <div className="flex justify-center gap-8 mt-8">
                <button
                  type="button"
                  className="flex flex-col items-center focus:outline-none"
                  onClick={handleGoogleLogin}
                  disabled={loadingState}
                  aria-label="Login with Google"
                >
                  <svg width="40" height="40" viewBox="0 0 48 48">
                    <g>
                      <circle cx="24" cy="24" r="24" fill="#fff" />
                      <path fill="#4285F4" d="M34.6 24.2c0-.7-.1-1.4-.2-2H24v4.1h6c-.3 1.5-1.3 2.7-2.7 3.5v2.9h4.4c2.6-2.4 4.1-5.9 4.1-10.5z" />
                      <path fill="#34A853" d="M24 36c3.6 0 6.6-1.2 8.8-3.2l-4.4-2.9c-1.2.8-2.7 1.3-4.4 1.3-3.4 0-6.2-2.3-7.2-5.4h-4.5v3.1C15.3 33.7 19.3 36 24 36z" />
                      <path fill="#FBBC05" d="M16.8 25.8c-.3-.8-.5-1.7-.5-2.8s.2-2 .5-2.8v-3.1h-4.5C11.3 19.3 10 21.5 10 24s1.3 4.7 3.3 6.1l4.5-3.1z" />
                      <path fill="#EA4335" d="M24 17.9c1.9 0 3.6.6 4.9 1.7l3.7-3.7C30.6 13.8 27.6 12.5 24 12.5c-4.7 0-8.7 2.3-11.2 5.7l4.5 3.1c1-3.1 3.8-5.4 7.2-5.4z" />
                    </g>
                  </svg>
                  <span className="text-xs mt-1">Google</span>
                </button>

                <button
                  type="button"
                  className="flex flex-col items-center focus:outline-none"
                  onClick={() => setShowPhoneLogin(true)}
                  aria-label="Login with Phone"
                >
                  <svg width="40" height="40" viewBox="0 0 48 48">
                    <circle cx="24" cy="24" r="24" fill="#fff" />
                    <path
                      d="M34 32.5v2.1c0 .8-.7 1.4-1.5 1.4-7.2 0-13-5.8-13-13 0-.8.6-1.5 1.4-1.5h2.1c.7 0 1.3.5 1.4 1.2.1.7.3 1.4.6 2 .2.5.1 1.1-.3 1.5l-1 1c1.2 2.3 3.1 4.2 5.4 5.4l1-1c.4-.4 1-.5 1.5-.3.6.2 1.3.5 2 .6.7.1 1.2.7 1.2 1.4z"
                      fill="#4CAF50"
                    />
                  </svg>
                  <span className="text-xs mt-1">Phone</span>
                </button>
              </div>
              </>
            )}

            <div className="mt-4 text-center">
              <span>Don&apos;t have an account? </span>
              <button
                type="button"
                className="text-primary font-semibold"
                onClick={() => navigate("/signup")}
              >
                Sign Up
              </button>
            </div>
          </div>
        ) : !otpSent ? (
          // Phone login UI (unchanged)
          <div>
            <h2 className="text-2xl font-bold mb-6 text-primary">Login with Phone</h2>
            {/* ... (same as your original phone login section) */}
            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4 rounded shadow-md">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <p className="text-red-800 font-semibold text-sm">Unable to Login</p>
                    <p className="text-red-700 text-sm mt-1">{error}</p>
                  </div>
                </div>
              </div>
            )}
            <form onSubmit={handleSendOtp}>
              <div className="mb-4 relative">
                <label className="block mb-1 font-semibold">Phone Number</label>
                <div className="flex">
                  <div className="relative">
                    <button
                      type="button"
                      className="flex items-center gap-2 px-3 py-2 border border-r-0 rounded-l bg-gray-50 hover:bg-gray-100"
                      onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                    >
                      <span>{selectedCountry.flag}</span>
                      <span className="text-sm">{selectedCountry.dial_code}</span>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {showCountryDropdown && (
                      <div className="absolute z-50 top-full left-0 w-64 max-h-60 overflow-y-auto bg-white border rounded-lg shadow-lg">
                        {countries.map((country) => (
                          <button
                            key={country.code}
                            type="button"
                            className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-100 text-left"
                            onClick={() => {
                              setSelectedCountry(country);
                              setShowCountryDropdown(false);
                            }}
                          >
                            <span>{country.flag}</span>
                            <span className="text-sm flex-1">{country.name}</span>
                            <span className="text-sm text-gray-500">{country.dial_code}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <input
                    type="tel"
                    className="flex-1 border rounded-r px-3 py-2"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="Enter phone number"
                    required
                  />
                </div>
              </div>
              <button
                type="submit"
                className="w-full bg-primary text-white py-2 rounded font-bold hover:bg-gray-300 hover:text-primary transition"
                disabled={loadingState}
              >
                {loadingState ? "Sending OTP..." : "Send OTP"}
              </button>
            </form>
            <div className="mt-4 text-center">
              <button
                type="button"
                className="text-primary font-semibold"
                onClick={handleBackToEmailLogin}
              >
                Back to Email Login
              </button>
            </div>
          </div>
        ) : (
          // OTP Screen (unchanged)
          <div>
            <h2 className="text-2xl font-bold mb-6 text-primary">Verify OTP</h2>
            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4 rounded shadow-md">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <p className="text-red-800 font-semibold text-sm">Unable to Login</p>
                    <p className="text-red-700 text-sm mt-1">{error}</p>
                  </div>
                </div>
              </div>
            )}
            <form onSubmit={handleVerifyOtp}>
              <div className="mb-4">
                <label className="block mb-1 font-semibold">Enter OTP</label>
                <input
                  type="text"
                  className="w-full border rounded px-3 py-2"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  required
                />
              </div>
              <button
                type="submit"
                className="w-full bg-primary text-white py-2 rounded font-bold hover:bg-green-700 transition"
                disabled={loadingState}
              >
                {loadingState ? "Verifying..." : "Verify OTP"}
              </button>
            </form>
            <div className="mt-4 text-center">
              <button
                type="button"
                className="text-primary font-semibold"
                onClick={handleBackToEmailLogin}
              >
                Back to Email Login
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;